import {Injectable, Injector} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import _ from 'lodash';

// Define the states as an enum
export enum OrderState {
    SelectTemplates = 'st',
    SelectProducts = 'sprd',
    CheckCart = 'cc',
    SelectSupplier = 'ss',
    SelectPromotion = 'sprm',
    VerifyPromotion = 'vprm',
    ConfirmOrder = 'co',
    OrderSubmited = 'os'
}

export enum StatePosition {
    Before,
    Current,
    After
}

@Injectable({
    providedIn: 'root',
})
export class OrderStateService {

    private readonly orderStateCacheKey = 'orderState'; // Key for ABP cache or session storage

    // BehaviorSubject to hold the current state
    private currentStateSubject: BehaviorSubject<OrderState>;

    constructor() {
        const state = this._getState();
        this.currentStateSubject = new BehaviorSubject<OrderState>(this.getOrderState(state) ?? OrderState.SelectTemplates);
    }

    // Observable to expose the current state
    get currentState$(): Observable<OrderState> {
        return this.currentStateSubject.asObservable();
    }

    // Method to get the current state value
    getCurrentState(): OrderState {
        return this.currentStateSubject.value;
    }

    // Method to transition to a new state
    setState(state: OrderState): void {
        this.currentStateSubject.next(state);
        this._setOrderState(state);
    }

    // Helper methods for specific state transitions
    selectProducts(): void {
        this.setState(OrderState.SelectProducts);
    }

    get isSelectProductsState(): boolean {
        return this.getCurrentState() === OrderState.SelectProducts;
    }

    checkCart(): void {
        this.setState(OrderState.CheckCart);
    }

    get isCheckOrderState(): boolean {
        return this.getCurrentState() === OrderState.CheckCart;
    }

    selectSupplier(): void {
        this.setState(OrderState.SelectSupplier);
    }

    get isSelectSupplierState(): boolean {
        return this.getCurrentState() === OrderState.SelectSupplier;
    }

    selectPromotion(): void {
        this.setState(OrderState.SelectPromotion);
    }

    get isSelectPromotionState(): boolean {
        return this.getCurrentState() === OrderState.SelectPromotion;
    }

    verifyPromotion(): void {
        this.setState(OrderState.VerifyPromotion);
    }

    get isVerifyPromotionState(): boolean {
        return this.getCurrentState() === OrderState.VerifyPromotion;
    }

    get isConfirmOrderState(): boolean {
        return this.getCurrentState() === OrderState.ConfirmOrder;
    }

    get isOrderSubmitedState(): boolean {
        return this.getCurrentState() === OrderState.OrderSubmited;
    }

    confirmOrder(): void {
        this.setState(OrderState.ConfirmOrder);
    }

    selectTemplates(): void {
        this.setState(OrderState.SelectTemplates);
    }

    //#region Cache

    private _getState(): string | undefined {
        const cachedState = abp.utils.getCookieValue(this.orderStateCacheKey) || sessionStorage.getItem(this.orderStateCacheKey);
        return cachedState || undefined;
    }

    private _setOrderState(state: OrderState): void {
        abp.utils.setCookieValue(this.orderStateCacheKey, state, null, abp.appPath);
        sessionStorage.setItem(this.orderStateCacheKey, state.toString());
    }

    reset(): void {
        abp.utils.setCookieValue(this.orderStateCacheKey, '', new Date(0), abp.appPath);
        sessionStorage.removeItem(this.orderStateCacheKey);

        this.selectTemplates();
    }

    //#endregion Cache

    getOrderState(value: string): OrderState | undefined {
        switch (value) {
            case 'st':
                return OrderState.SelectTemplates;
            case 'sprd':
                return OrderState.SelectProducts;
            case 'cc':
                return OrderState.CheckCart;
            case 'ss':
                return OrderState.SelectSupplier;
            case 'sprm':
                return OrderState.SelectPromotion;
            case 'vprm':
                return OrderState.VerifyPromotion;
            case 'co':
                return OrderState.ConfirmOrder;
            case 'os':
                return OrderState.OrderSubmited;
            default:
                return undefined; // Handle invalid input
        }
    }
}
